import React from 'react';

import { Species } from '../../api/fishrules/getSpeciesAtLocation';
import { hasRestrictions } from '../../components/ProhibitedMethods/helpers';
import { ProhibitedMethods } from '../../components/ProhibitedMethods/ProhibitedMethods';
import { Text } from '../../components/Text/Text';
import { convertHtmlToReactElement } from '../../components/TextFromHTML/helpers';
import { TextFromHTML } from '../../components/TextFromHTML/TextFromHTML';
import { IBoundary } from '../hooks/fishrules/useRegulationBoundaryAtLocation';

interface AdditionalInformation {
  order: number;
  visible: boolean;
  title: string;
  body: React.JSX.Element;
}

const getAdditionalInformation = ({
  species,
  boundaries,
}: {
  species: Species;
  boundaries: IBoundary[];
}): AdditionalInformation[] => {
  const locationSpecificInformation = boundaries
    .map(({ regulationNotes }) => regulationNotes)
    .filter(regulation => regulation);

  const ADDITIONAL_INFORMATION: Record<string, AdditionalInformation> = {
    prohibitedMethods: {
      order: 1,
      visible: hasRestrictions(species),
      title: 'Restrictions & requirements',
      body: <ProhibitedMethods species={species} />,
    },
    requiredLicenses: {
      order: 2,
      visible: Boolean(species.additional_licenses_required),
      title: 'Required licenses',
      body: <TextFromHTML html={species.additional_licenses_required} />,
    },
    locationRegulationNotes: {
      order: 3,
      visible: Boolean(species.location_regulation_notes),
      title: 'Location regulation notes',
      body: <TextFromHTML html={species.location_regulation_notes} />,
    },
    additionalInformation: {
      order: 4,
      visible: Boolean(species.notes),
      title: 'Additional information',
      body: <div>{convertHtmlToReactElement(species.notes, ['a'])}</div>,
    },
    edibility: {
      order: 5,
      visible: Boolean(species.edibility),
      title: 'Edibility',
      body: <TextFromHTML html={species.edibility} />,
    },
    synonyms: {
      order: 6,
      visible: species.synonyms.length > 0,
      title: 'Synonyms',
      body: <Text className="mb-0">{species.synonyms.join(', ')}</Text>,
    },
    locationSpecificInformation: {
      order: 7,
      visible: locationSpecificInformation.length > 0,
      title: 'Location specific information',
      body: (
        <>
          {locationSpecificInformation.map(info => (
            <TextFromHTML key={info} html={info} />
          ))}
        </>
      ),
    },
  };

  return Object.values(ADDITIONAL_INFORMATION).sort(o => o.order);
};

export { getAdditionalInformation };
